import { useEffect, useMemo, useState } from "react";
import { api } from "../api/client";
import type { AnalysisPlugin, HandFrames, Job, Joint3D } from "../api/types";
import { useApp } from "../state/AppContext";
import { Badge, KeyValue, ProgressBar, Spinner } from "./ui";

/**
 * Running analyses and reading what they produced.
 *
 * Every result is shown with the thing that makes it usable or not: how much of
 * the session was actually analysed, what geometry it used, and whether that
 * geometry is measured or assumed.
 */
export function AnalysisPanel({
  sessionId,
  phoneTest = false,
  plugins,
  jobs,
  hands,
  calibration,
  currentSample,
  onRefresh,
}: {
  sessionId: string;
  phoneTest?: boolean;
  plugins: AnalysisPlugin[];
  jobs: Job[];
  hands: HandFrames | null;
  calibration: { profile: string | null; status: string; is_measured: boolean; estimate?: any };
  currentSample: { hands: number | null };
  onRefresh: () => void;
}) {
  const { notify, refreshJobs } = useApp();
  // "" means every frame — the default, because a result every eleventh frame
  // either jumps on screen or is held over content that has moved.
  const [coverage, setCoverage] = useState<"" | "240" | "120" | "60">("");
  const [activityWindow, setActivityWindow] = useState(8);
  const activityPlugin = plugins.find(p => p.id === "activities.temporal");
  const [detectArms, setDetectArms] = useState(true);
  const [squareMm, setSquareMm] = useState(25);
  const [calibrationSource, setCalibrationSource] = useState("");
  const [calibrationCaptures, setCalibrationCaptures] = useState<{id:string;name:string}[]>([]);
  useEffect(() => {void api.sessions().then(r=>setCalibrationCaptures(r.sessions.filter(s=>s.id!==sessionId && s.calibration_profile === "session:checkerboard"))).catch(()=>undefined);},[sessionId]);
  const [busy, setBusy] = useState<string | null>(null);

  const jobFor = (pluginId: string) =>
    jobs.find((j) => j.params?.plugin_id === pluginId && (j.state === "queued" || j.state === "running"));

  const activeJob = (kind: string) =>
    jobs.find((j) => j.kind === kind && (j.state === "queued" || j.state === "running"));

  const pipelineJob = jobs.find(j => ["local_pipeline", "upload_ingest"].includes(j.kind) && ["queued", "running"].includes(j.state));

  const run = async (plugin: AnalysisPlugin, force: boolean) => {
    setBusy(plugin.id);
    try {
      const settings: Record<string, unknown> = {
        max_frames: coverage === "" ? null : Number(coverage),
      };
      if (plugin.id === "activities.temporal") {delete settings.max_frames;settings.window_seconds=activityWindow;}
      if (plugin.id === "objects.yolox") settings.sample_fps = coverage === "" ? null : 2;
      if (plugin.id === "hands.mediapipe") settings.detect_arms = detectArms;
      const r = await api.runAnalysis(sessionId, plugin.id, settings, force);
      notify(r.reused ? `Reused the cached result for ${plugin.display_name}` : `Running ${plugin.display_name}…`);
      onRefresh();
    } catch (e: any) {
      notify(e.message, "error");
    } finally {
      setBusy(null);
    }
  };

  return (
    <div className="analysis-panel">
      <div className="panel pad">
        <strong>Local analysis pipeline</strong>
        <p className="tiny muted">{phoneTest ? "Phone test: objects, faces and 2D hands run automatically. Calibration is unavailable because both views come from one camera." : "Uploads automatically run sync checks, objects, faces, hands, activity chapters and full-frame AI depth. Results appear as each pass finishes. Originals and results stay on this server."}</p>
        <button disabled={!!pipelineJob || busy === "pipeline"} className="primary" onClick={async () => {setBusy("pipeline");try{await api.pipeline(sessionId);notify("Local pipeline started");onRefresh();}catch(e){notify((e as Error).message,"error");}finally{setBusy(null);}}}>Reprocess all</button>
        {pipelineJob && <><p className="tiny muted">{pipelineJob.message || pipelineJob.state}</p><ProgressBar value={pipelineJob.progress} state={pipelineJob.state}/></>}
        {jobs.filter(j => ["local_pipeline", "analysis", "upload_ingest"].includes(j.kind) &&
          (["failed", "cancelled"].includes(j.state) || (j.result?.state ?? j.result?.pipeline?.state) === "needs_attention")).slice(0,3).map(j =>
          <div key={j.id} className="panel pad"><p className="tiny">{j.error || "일부 분석이 완료되지 않았습니다"}</p>
            <button disabled={!!pipelineJob || busy === j.id} onClick={async () => {
              setBusy(j.id); try { await api.resumeJob(j.id); refreshJobs(); onRefresh(); notify("완료된 단계를 확인하고 재개합니다"); }
              catch(e) { notify((e as Error).message, "error"); } finally { setBusy(null); }
            }}>Resume · 이어서 처리</button></div>)}
        <p className="tiny muted">Frame analyses cover every frame. Activity chapters use chronological image samples and merge sustained tasks.</p>
      </div>
      {activityPlugin && <section className="panel pad">
        <strong>Automatic activity chapters</strong>
        <p className="tiny muted">{activityPlugin.model_name}. One context per environment, with shorter chapters when the task changes. Human edits are preserved.</p>
        <label>Evidence window <select aria-label="Activity evidence window" value={activityWindow} onChange={e=>setActivityWindow(Number(e.target.value))}>
          <option value={4}>4 seconds · finer changes</option><option value={8}>8 seconds · balanced</option><option value={12}>12 seconds · longer tasks</option>
        </select></label>
        <button disabled={!!pipelineJob || !!jobFor(activityPlugin.id) || busy===activityPlugin.id} onClick={()=>void run(activityPlugin,true)}>Refresh activity labels</button>
        {jobFor(activityPlugin.id) && <><p className="tiny muted">{jobFor(activityPlugin.id)?.message}</p><ProgressBar value={jobFor(activityPlugin.id)!.progress} state={jobFor(activityPlugin.id)!.state}/></>}
        {activityPlugin.result_summary && <p className="tiny muted">Model proposals: {activityPlugin.result_summary.contexts} context · {activityPlugin.result_summary.activities} activities · {activityPlugin.result_summary.engine === "local_vlm" ? "Local visual-language model" : "Evidence rules"}. Saved chapters may split around your edits.</p>}
      </section>}
      {!phoneTest && <details className="panel pad"><summary>Camera calibration</summary>
      <div className="panel pad">
        <strong>Checkerboard calibration</strong>
        <p className="tiny muted">Lens spacing: 134.5 mm; downward pitch: 7°. Record and upload 25–35 checkerboard poses visible to both cameras. Keep the glasses rigidly fixed and hold each board pose for 5 seconds.</p>
        <a href="/checkerboard.html" target="_blank" rel="noreferrer">Print A4 checkerboard · 9×6 inner corners</a>
        <label style={{marginTop:8}}>Measured square size (mm) <input aria-label="Checkerboard square mm" type="number" min={5} max={100} value={squareMm} onChange={e=>setSquareMm(Number(e.target.value))} /></label>
        <button disabled={!!pipelineJob || !!activeJob("calibrate_checkerboard") || busy === "calibration"} onClick={async()=>{setBusy("calibration");try{await api.calibrate(sessionId,{columns:9,rows:6,square_mm:squareMm,baseline_mm:134.5,pitch_down_deg:7});notify("Calibration started · Applied only if validation thresholds pass");onRefresh();}catch(e){notify((e as Error).message,"error");}finally{setBusy(null);}}}>Calibrate from this capture</button>
        {jobs.filter(j=>j.kind==="calibrate_checkerboard").slice(0,1).map(j=><p className="tiny" key={j.id}>{j.state} · {j.error || j.message}</p>)}
        <label>Calibration capture from the same glasses
          <select aria-label="Calibration capture" value={calibrationSource} onChange={e=>setCalibrationSource(e.target.value)}>
            <option value="">Choose a capture with accepted calibration</option>
            {calibrationCaptures.map(s=><option key={s.id} value={s.id}>{s.name}</option>)}
          </select>
        </label>
        <button disabled={!!pipelineJob || !calibrationSource || busy === "reuse"} onClick={async()=>{setBusy("reuse");try{await api.useCalibration(sessionId,calibrationSource);notify("Calibration applied; analysis restarted");onRefresh();}catch(e){notify((e as Error).message,"error");}finally{setBusy(null);}}}>Apply calibration & reprocess</button>
        <p className="tiny muted">Calibration is valid at this resolution. Accepted calibration files and error reports are saved with the capture.</p>
      </div>
      <CalibrationCard sessionId={sessionId} calibration={calibration}
        job={activeJob("estimate_extrinsics")} onRefresh={onRefresh} />
      </details>}
      <details className="panel pad"><summary>Individual analyses & settings</summary>
      <p className="tiny muted">These settings apply to the individual Run buttons below.</p>
      <div className="col" style={{ gap: 6, flexShrink: 0 }}>
        <div className="row">
          <label style={{ margin: 0, minWidth: 62 }}>Coverage</label>
          <select value={coverage} onChange={(e) => setCoverage(e.target.value as any)} style={{ width: 180 }}>
            <option value="">Every frame</option>
            <option value="240">240 frames, sampled</option>
            <option value="120">120 frames, sampled</option>
            <option value="60">60 frames, sampled</option>
          </select>
        </div>
        <div className="tiny muted">
          {coverage === ""
            ? "Objects, faces and AI depth use every frame; hands use every valid stereo pair. Processing continues in the background."
            : "A quick look — the overlay will jump between samples."}
        </div>
        <div className="row">
          <label style={{ margin: 0, minWidth: 62 }}>Arms</label>
          <button className={detectArms ? "active" : "ghost"} onClick={() => setDetectArms((v) => !v)}>
            {detectArms ? "Detect arms" : "Hands only"}
          </button>
          <span className="tiny muted">
            {detectArms ? "roughly doubles the hand pass" : "skips the pose model"}
          </span>
        </div>
      </div>



      {plugins.filter(p => p.id !== "depth.stereo_sgbm").map((plugin) => {
        const job = jobFor(plugin.id);
        return (
          <div key={plugin.id} className="panel" style={{ padding: 0, flexShrink: 0 }}>
            <div className="panel-head" style={{ gap: 6 }}>
              <span style={{ textTransform: "none" }}>{plugin.display_name}</span>
              <Badge kind={plugin.state === "available" ? "good" : "warning"}>{plugin.state}</Badge>
              <span className="spacer" />
              <span className="tiny muted mono">v{plugin.version}</span>
            </div>
            <div style={{ padding: 10 }}>
              <div className="tiny muted" style={{ marginBottom: 8, lineHeight: 1.45 }}>
                {plugin.description}
              </div>
              {plugin.state !== "available" ? (
                <div className="error-box tiny" style={{ marginBottom: 8 }}>{plugin.reason}</div>
              ) : null}

              {job ? (
                <div className="col" style={{ gap: 4, marginBottom: 8 }}>
                  <div className="row tiny muted"><Spinner size={10} /> {job.message || job.state}</div>
                  <div className="row">
                    <ProgressBar value={job.progress} state={job.state} />
                    <button className="ghost" onClick={() => api.cancelJob(job.id).then(onRefresh)}>Cancel</button>
                  </div>
                </div>
              ) : (
                <div className="row" style={{ gap: 6, marginBottom: 8 }}>
                  <button
                    className="primary"
                    disabled={!!pipelineJob || plugin.state !== "available" || busy === plugin.id}
                    onClick={() => run(plugin, plugin.has_result)}
                  >
                    {plugin.has_result ? "Run again" : "Run"}
                  </button>
                  {plugin.has_result ? <Badge kind="good">result ready</Badge> : <Badge kind="neutral">not run</Badge>}
                </div>
              )}

              <details>
                <summary className="tiny muted" style={{ cursor: "pointer" }}>Provenance</summary>
                <div style={{ marginTop: 6 }}>
                  <KeyValue
                    rows={[
                      ["Model", plugin.model_name ?? "—"],
                      ["Weights", plugin.weights_version ?? "—"],
                      ["Licence", plugin.license ?? "—"],
                      ["Runtime", plugin.runtime ?? "—"],
                      ["Cost", plugin.compute_estimate ?? "—"],
                      ["Needs calibration", plugin.requires_calibration ? "yes" : "no"],
                      ["Streams", plugin.required_streams.join(", ")],
                    ]}
                  />
                </div>
              </details>

              {plugin.id === "objects.yolox" && plugin.result_summary ? <div className="tiny">
                <p>{plugin.result_summary.detections} detections · {plugin.result_summary.tracks} tracks · {plugin.result_summary.relation_candidates} relation candidates</p>
                {plugin.result_summary.continuity && <p className="muted">{plugin.result_summary.continuity.low_score_observations} weak detections retained · {plugin.result_summary.continuity.interpolated_boxes} boxes estimated between observations. Estimated boxes use dashed outlines.</p>}
                <p className="muted">The viewer suppresses weak or fleeting detections using confidence and repeated observations. Raw detections are retained for export. Enable Objects to inspect boxes. Object tracks stay on the image. Activity chapters combine sustained context and hand evidence.</p>
                <a href={`/api/v1/sessions/${sessionId}/files/reports/upload-plan.json`} target="_blank" rel="noreferrer">Local upload plan</a>
              </div> : null}
              {plugin.id === "hands.mediapipe" && hands ? <HandSummary hands={hands} /> : null}

            </div>
          </div>
        );
      })}

      {hands ? <Joints3D hands={hands} sampleIndex={currentSample.hands} /> : null}
      </details>
    </div>
  );
}

/**
 * The calibration, and the one thing that can be done about it here.
 *
 * The shipped profile assumes the cameras are parallel. They are not — measured
 * on a real recording they are a couple of degrees out, which moves matching
 * rows by 10-24 px, and block matching searches along rows. So this is not a
 * refinement: without it stereo depth is wrong away from the centre.
 */
function CalibrationCard({
  sessionId, calibration, job, onRefresh,
}: { sessionId: string; calibration: any; job?: Job; onRefresh: () => void }) {
  const { notify } = useApp();
  const [busy, setBusy] = useState(false);
  const estimate = calibration.estimate;
  const residual = estimate?.epipolar_residual_px;

  const run = async () => {
    setBusy(true);
    try {
      await api.estimateExtrinsics(sessionId);
      notify("Estimating the cameras' relative pose from this recording…");
      onRefresh();
    } catch (e: any) {
      notify(e.message, "error");
    } finally {
      setBusy(false);
    }
  };

  const state = calibration.is_measured
    ? "good"
    : calibration.status === "estimated_from_recording"
      ? "info"
      : "warning";

  return (
    <div className="panel" style={{ padding: 0, flexShrink: 0 }}>
      <div className="panel-head" style={{ gap: 6 }}>
        <span style={{ textTransform: "none" }}>Calibration</span>
        <Badge kind={state}>{String(calibration.status ?? "unknown").replace(/_/g, " ")}</Badge>
        <span className="spacer" />
        {job ? (
          <div className="row" style={{ width: 130 }}>
            <Spinner size={10} />
            <ProgressBar value={job.progress} state={job.state} />
          </div>
        ) : (
          <button onClick={run} disabled={busy}>
            {estimate ? "Re-estimate" : "Estimate from recording"}
          </button>
        )}
      </div>
      <div style={{ padding: 10 }}>
        {!estimate ? (
          <div className="tiny muted" style={{ lineHeight: 1.5 }}>
            The profile assumes the two cameras are parallel. They usually are not, and block
            matching searches along image rows — so a couple of degrees is enough to make depth
            wrong away from the centre. This measures the rotation and the translation direction
            from the footage itself. The baseline length and the intrinsics stay assumed: image
            pairs cannot determine scale.
          </div>
        ) : (
          <>
            <KeyValue
              rows={[
                ["Row alignment", residual
                  ? `${residual.before.mean_abs_px.toFixed(2)} px → ${residual.after.mean_abs_px.toFixed(2)} px (${estimate.improvement}× better)`
                  : "—"],
                ["Rotation found", `${estimate.rotation_deg.map((a: number) => a.toFixed(2)).join("°, ")}°`],
                ["Baseline", `${(estimate.baseline_m * 100).toFixed(1)} cm (assumed, from the device config)`],
                ["From", `${estimate.frames_used} frames · ${estimate.inliers} inliers`],
              ]}
            />
            <div className="tiny muted" style={{ marginTop: 8, lineHeight: 1.45 }}>
              Measured: {estimate.measured?.join("; ")}.<br />
              Still assumed: {estimate.assumed?.join("; ")}.
            </div>
          </>
        )}
      </div>
    </div>
  );
}

function coverageLabel(summary: any) {
  const plan = summary?.plan ?? {};
  if (plan.analysed == null) return "—";
  if (plan.every_frame) return `every frame · ${plan.analysed} pairs`;
  return `${plan.analysed} of ${plan.usable_pairs} pairs (every ${plan.stride}th)`;
}

function HandSummary({ hands }: { hands: HandFrames }) {
  const s = hands.summary;
  return (
    <div style={{ marginTop: 10 }}>
      <KeyValue
        rows={[
          ["Coverage", coverageLabel(s)],
          ["Hand detections", `${s.hands?.detections_2d ?? 0} across both eyes`],
          ["Triangulated", `${s.hands?.triangulated_landmarks ?? 0} landmarks`],
          ["Wrist agreement", s.hands?.median_wrist_epipolar_px != null
            ? `${s.hands.median_wrist_epipolar_px.toFixed(1)} px median from the epipolar line`
            : "—"],
          ["Arm joints", `${s.arms?.accepted ?? 0} accepted / ${s.arms?.candidates ?? 0} candidates`],
          ["Arm agreement", s.arms?.median_epipolar_px != null
            ? `${s.arms.median_epipolar_px.toFixed(1)} px median (limit ${s.arms.agreement_threshold_px?.toFixed(1)} px)`
            : "—"],
        ]}
      />
      {s.arms?.rejection_reasons ? (
        <div className="tiny muted" style={{ marginTop: 6 }}>
          {Object.entries(s.arms.rejection_reasons as Record<string, number>).map(([reason, n]) => (
            <div key={reason}>· {n} × {reason}</div>
          ))}
        </div>
      ) : null}
      {hands.arms_caveat ? (
        <div className="tiny" style={{ color: "var(--warn)", marginTop: 8, lineHeight: 1.45 }}>
          {hands.arms_caveat}
        </div>
      ) : null}
    </div>
  );
}

/**
 * The triangulated joints for the current instant.
 *
 * Shown as two orthographic projections rather than a perspective scene: the
 * full 3D viewer is phase 5, and a top-down plus side view is enough to see
 * whether a hand is where it should be without pretending to be one.
 */
function Joints3D({ hands, sampleIndex }: { hands: HandFrames; sampleIndex: number | null }) {
  const sample = sampleIndex != null ? hands.samples[sampleIndex] : null;
  const joints = useMemo(() => {
    if (!sample) return { hands: [] as Joint3D[], arms: [] as Joint3D[] };
    return {
      hands: sample.hands3d.filter((j) => j.valid && j.x_m != null),
      arms: sample.arms3d.filter((j) => j.accepted && j.x_m != null),
    };
  }, [sample]);

  if (!sample) return null;
  const all = [...joints.hands, ...joints.arms];

  return (
    <div className="panel" style={{ padding: 0, flexShrink: 0 }}>
      <div className="panel-head" style={{ gap: 6 }}>
        <span style={{ textTransform: "none" }}>3D joints · device frame</span>
        {hands.approximate_3d ? <Badge kind="warning">Approximate / uncalibrated</Badge> : <Badge kind="good">calibrated</Badge>}
      </div>
      <div style={{ padding: 10 }}>
        {all.length === 0 ? (
          <div className="hint">Nothing triangulated at this instant.</div>
        ) : (
          <>
            <div className="row" style={{ gap: 8 }}>
              <Projection title="top (X forward, Y left)" points={all} ax="x_m" ay="y_m" flipY />
              <Projection title="side (X forward, Z up)" points={all} ax="x_m" ay="z_m" />
            </div>
            <div className="tiny muted mono" style={{ marginTop: 8 }}>
              {joints.hands.length ? (
                <div>
                  wrist: {fmt(joints.hands.find((j) => j.landmark === 0))}
                </div>
              ) : null}
              {joints.arms.map((j) => (
                <div key={j.name}>{j.name}: {fmt(j)}</div>
              ))}
            </div>
            <div className="tiny muted" style={{ marginTop: 6, lineHeight: 1.45 }}>
              X forward, Y left, Z up, metres, origin midway between the cameras. These are stereo
              triangulations — not MediaPipe's monocular world landmarks, which are stored separately.
            </div>
          </>
        )}
      </div>
    </div>
  );
}

function fmt(j: Joint3D | undefined) {
  if (!j || j.x_m == null) return "—";
  return `X ${j.x_m.toFixed(3)}  Y ${j.y_m!.toFixed(3)}  Z ${j.z_m!.toFixed(3)} m` +
    (j.reproj_px != null ? `  (reproj ${j.reproj_px.toFixed(1)} px)` : "");
}

function Projection({
  title, points, ax, ay, flipY,
}: { title: string; points: Joint3D[]; ax: "x_m"; ay: "y_m" | "z_m"; flipY?: boolean }) {
  const size = 118;
  const pad = 10;
  const xs = points.map((p) => p[ax] as number);
  const ys = points.map((p) => p[ay] as number);
  const minX = Math.min(0, ...xs);
  const maxX = Math.max(0.6, ...xs);
  const spread = Math.max(0.35, ...ys.map(Math.abs)) * 1.15;
  const px = (v: number) => pad + ((v - minX) / Math.max(1e-6, maxX - minX)) * (size - 2 * pad);
  const py = (v: number) => {
    const t = (v / spread + 1) / 2;
    return pad + (flipY ? t : 1 - t) * (size - 2 * pad);
  };
  return (
    <div>
      <div className="tiny muted" style={{ marginBottom: 3 }}>{title}</div>
      <svg width={size} height={size} style={{ background: "var(--bg-0)", border: "1px solid var(--line)", borderRadius: 3 }}>
        <line x1={pad} y1={size / 2} x2={size - pad} y2={size / 2} stroke="var(--line-strong)" />
        <line x1={px(0)} y1={pad} x2={px(0)} y2={size - pad} stroke="var(--line-strong)" />
        <circle cx={px(0)} cy={size / 2} r={3} fill="var(--accent)" />
        {points.map((p, i) => (
          <circle
            key={i}
            cx={px(p[ax] as number)}
            cy={py(p[ay] as number)}
            r={p.name ? 3.5 : 2}
            fill={p.name ? "#9be08a" : p.handedness === "left" ? "#4cc4ff" : "#ffb35c"}
          />
        ))}
        <text x={px(0) + 3} y={size - 3} fill="var(--text-3)" fontSize={8} fontFamily="ui-monospace">device</text>
      </svg>
    </div>
  );
}
